import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Route,
  Truck,
  User,
  Clock,
  MapPin,
  Building,
  Calendar,
  Package,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  Eye,
  MoreHorizontal,
  Settings,
  RefreshCw,
  Download,
  Upload,
  Users,
  Activity,
  TrendingUp,
  Zap,
  X,
  Copy,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';

// Mock data cho tuyến đường - Mở rộng với nhiều tuyến đường đa dạng
const mockRoutes = [
  {
    id: 'route-001',
    name: 'Tuyến Hà Nội - Hải Phòng',
    code: 'HN-HP-01',
    path: 'Hà Nội → Hải Dương → Hải Phòng',
    startPoint: 'Bến xe Mỹ Đình, Hà Nội', // Điểm đầu tự nhập
    endPoint: 'Bến xe Lạch Tray, Hải Phòng', // Điểm cuối tự nhập
    startLocation: { id: 'hub-001', name: 'Bưu cục Hà Nội' },
    endLocation: { id: 'hub-002', name: 'Bưu cục Hải Phòng' },
    distance: 120.5,
    estimatedTime: 150,
    operatingStartTime: '06:00', // Giờ bắt đầu hoạt động
    operatingEndTime: '22:00', // Giờ kết thúc hoạt động
    breakStartTime: '12:00', // Giờ bắt đầu nghỉ
    breakEndTime: '13:00', // Giờ kết thúc nghỉ
    operatingHours: '06:00 - 22:00',
    roundTripTime: 30,
    status: 'active',
    vehicleCount: 8,
    driverCount: 12,
    monthlyTrips: 240,
    createdAt: '2024-01-15T08:00:00Z',
    notes: 'Tuyến chính, ưu tiên cao'
  },
  {
    id: 'route-002',
    name: 'Tuyến TP.HCM - Đà Nẵng',
    code: 'HCM-DN-02',
    path: 'TP.HCM → Nha Trang → Đà Nẵng',
    startPoint: 'Bến xe Miền Đông, TP.HCM',
    endPoint: 'Bến xe Đà Nẵng',
    startLocation: { id: 'hub-003', name: 'Bưu cục TP.HCM' },
    endLocation: { id: 'hub-004', name: 'Bưu cục Đà Nẵng' },
    distance: 950.0,
    estimatedTime: 720,
    operatingStartTime: '05:00',
    operatingEndTime: '23:00',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '05:00 - 23:00',
    roundTripTime: 45,
    status: 'active',
    vehicleCount: 12,
    driverCount: 18,
    monthlyTrips: 180,
    createdAt: '2024-01-10T09:30:00Z',
    notes: 'Tuyến liên tỉnh quan trọng'
  },
  {
    id: 'route-003',
    name: 'Tuyến Hà Nội - Vinh',
    code: 'HN-V-03',
    path: 'Hà Nội → Nam Định → Thanh Hóa → Vinh',
    startPoint: 'Bến xe Giáp Bát, Hà Nội',
    endPoint: 'Bến xe Vinh, Nghệ An',
    startLocation: { id: 'hub-001', name: 'Bưu cục Hà Nội' },
    endLocation: { id: 'hub-005', name: 'Bưu cục Vinh' },
    distance: 320.0,
    estimatedTime: 360,
    operatingStartTime: '06:00',
    operatingEndTime: '20:00',
    breakStartTime: '12:30',
    breakEndTime: '13:30',
    operatingHours: '06:00 - 20:00',
    roundTripTime: 40,
    status: 'maintenance',
    vehicleCount: 6,
    driverCount: 8,
    monthlyTrips: 120,
    createdAt: '2024-01-05T07:15:00Z',
    notes: 'Đang bảo trì tuyến'
  },
  {
    id: 'route-004',
    name: 'Tuyến Hà Nội - Thái Bình',
    code: 'HN-TB-04',
    path: 'Hà Nội → Hưng Yên → Thái Bình',
    startPoint: 'Bến xe Gia Lâm, Hà Nội',
    endPoint: 'Bến xe Thái Bình',
    startLocation: { id: 'hub-001', name: 'Bưu cục Hà Nội' },
    endLocation: { id: 'hub-006', name: 'Bưu cục Thái Bình' },
    distance: 110.0,
    estimatedTime: 120,
    operatingStartTime: '05:30',
    operatingEndTime: '22:30',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '05:30 - 22:30',
    roundTripTime: 25,
    status: 'active',
    vehicleCount: 4,
    driverCount: 6,
    monthlyTrips: 320,
    createdAt: '2024-01-12T06:45:00Z',
    notes: 'Tuyến ngắn, tần suất cao'
  },
  {
    id: 'route-005',
    name: 'Tuyến TP.HCM - Cần Thơ',
    code: 'HCM-CT-05',
    path: 'TP.HCM → Mỹ Tho → Vĩnh Long → Cần Thơ',
    startPoint: 'Bến xe Miền Tây, TP.HCM',
    endPoint: 'Bến xe Cần Thơ',
    startLocation: { id: 'hub-003', name: 'Bưu cục TP.HCM' },
    endLocation: { id: 'hub-007', name: 'Bưu cục Cần Thơ' },
    distance: 170.0,
    estimatedTime: 180,
    operatingStartTime: '05:00',
    operatingEndTime: '22:00',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '05:00 - 22:00',
    roundTripTime: 30,
    status: 'active',
    vehicleCount: 5,
    driverCount: 8,
    monthlyTrips: 200,
    createdAt: '2024-01-08T10:20:00Z',
    notes: 'Tuyến miền Tây, hàng nông sản'
  },
  {
    id: 'route-006',
    name: 'Tuyến Đà Nẵng - Huế',
    code: 'DN-HUE-06',
    path: 'Đà Nẵng → Lăng Cô → Huế',
    startPoint: 'Bến xe Đà Nẵng',
    endPoint: 'Bến xe An Hòa, Huế',
    startLocation: { id: 'hub-004', name: 'Bưu cục Đà Nẵng' },
    endLocation: { id: 'hub-008', name: 'Bưu cục Huế' },
    distance: 105.0,
    estimatedTime: 150,
    operatingStartTime: '06:00',
    operatingEndTime: '21:00',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '06:00 - 21:00',
    roundTripTime: 45,
    status: 'active',
    vehicleCount: 3,
    driverCount: 5,
    monthlyTrips: 160,
    createdAt: '2024-01-15T14:30:00Z',
    notes: 'Tuyến du lịch, cảnh đẹp'
  },
  {
    id: 'route-007',
    name: 'Tuyến Hà Nội - Lào Cai',
    code: 'HN-LC-07',
    path: 'Hà Nội → Vĩnh Phúc → Yên Bái → Lào Cai',
    startPoint: 'Bến xe Mỹ Đình, Hà Nội',
    endPoint: 'Bến xe Lào Cai',
    startLocation: { id: 'hub-001', name: 'Bưu cục Hà Nội' },
    endLocation: { id: 'hub-009', name: 'Bưu cục Lào Cai' },
    distance: 350.0,
    estimatedTime: 420,
    operatingStartTime: '05:00',
    operatingEndTime: '20:00',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '05:00 - 20:00',
    roundTripTime: 60,
    status: 'active',
    vehicleCount: 4,
    driverCount: 6,
    monthlyTrips: 100,
    createdAt: '2024-01-20T08:15:00Z',
    notes: 'Tuyến miền núi, đường khó'
  },
  {
    id: 'route-008',
    name: 'Tuyến TP.HCM - Vũng Tàu',
    code: 'HCM-VT-08',
    path: 'TP.HCM → Biên Hòa → Vũng Tàu',
    startPoint: 'Bến xe Miền Đông, TP.HCM',
    endPoint: 'Bến xe Vũng Tàu',
    startLocation: { id: 'hub-003', name: 'Bưu cục TP.HCM' },
    endLocation: { id: 'hub-010', name: 'Bưu cục Vũng Tàu' },
    distance: 125.0,
    estimatedTime: 140,
    operatingStartTime: '05:30',
    operatingEndTime: '23:30',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    operatingHours: '05:30 - 23:30',
    roundTripTime: 35,
    status: 'active',
    vehicleCount: 6,
    driverCount: 9,
    monthlyTrips: 280,
    createdAt: '2024-01-18T11:00:00Z',
    notes: 'Tuyến biển, du lịch cao'
  }
];

// Mock data cho phương tiện
const mockVehicles = [
  {
    id: 'vehicle-001',
    licensePlate: '29A-12345',
    type: { id: 'type-001', name: 'Xe tải nhỏ' },
    brand: 'Hyundai',
    model: 'Porter H150',
    capacity: 1500,
    route: { id: 'route-001', name: 'Tuyến Hà Nội - Hải Phòng' },
    driver: { id: 'driver-001', name: 'Nguyễn Văn A', phone: '0987654321' },
    status: 'active',
    condition: 'good',
    registrationDate: '2023-01-15',
    lastMaintenanceDate: '2024-01-10',
    nextMaintenanceDate: '2024-04-10',
    fuelType: 'diesel',
    manufacturingYear: 2022,
    insurance: { provider: 'Bảo Việt', expiryDate: '2024-12-31' },
    notes: 'Xe mới, tình trạng tốt'
  },
  {
    id: 'vehicle-002',
    licensePlate: '51B-67890',
    type: { id: 'type-002', name: 'Xe tải trung' },
    brand: 'Isuzu',
    model: 'NPR 400',
    capacity: 3500,
    route: { id: 'route-002', name: 'Tuyến TP.HCM - Đà Nẵng' },
    driver: { id: 'driver-002', name: 'Trần Văn B', phone: '0909876543' },
    status: 'maintenance',
    condition: 'fair',
    registrationDate: '2022-08-20',
    lastMaintenanceDate: '2024-01-05',
    nextMaintenanceDate: '2024-04-05',
    fuelType: 'diesel',
    manufacturingYear: 2021,
    insurance: { provider: 'PTI', expiryDate: '2024-08-20' },
    notes: 'Đang bảo trì định kỳ'
  },
  {
    id: 'vehicle-003',
    licensePlate: '30C-11111',
    type: { id: 'type-001', name: 'Xe tải nhỏ' },
    brand: 'Ford',
    model: 'Transit',
    capacity: 1200,
    route: { id: 'route-001', name: 'Tuyến Hà Nội - Hải Phòng' },
    driver: { id: 'driver-003', name: 'Lê Văn C', phone: '0912345678' },
    status: 'active',
    condition: 'excellent',
    registrationDate: '2023-06-01',
    lastMaintenanceDate: '2024-01-15',
    nextMaintenanceDate: '2024-04-15',
    fuelType: 'diesel',
    manufacturingYear: 2023,
    insurance: { provider: 'Bảo Minh', expiryDate: '2024-06-01' },
    notes: 'Xe mới nhập'
  }
];

// Mock data cho tài xế
const mockDrivers = [
  {
    id: 'driver-001',
    name: 'Nguyễn Văn A',
    username: 'nguyenvana', // Tài khoản đăng nhập
    password: 'password123', // Mật khẩu
    phone: '0987654321',
    email: 'nguyenvana@company.com',
    licenseNumber: 'B2-123456789',
    licenseClass: 'B2',
    licenseExpiryDate: '2026-12-31',
    status: 'active',
    currentRoute: { id: 'route-001', name: 'Tuyến Hà Nội - Hải Phòng' },
    currentVehicle: { id: 'vehicle-001', licensePlate: '29A-12345' },
    assignedVehicles: ['vehicle-001'], // Danh sách xe được gán
    joinDate: '2023-02-01',
    totalTrips: 156,
    rating: 4.8,
    notes: 'Tài xế kinh nghiệm, đáng tin cậy'
  },
  {
    id: 'driver-002',
    name: 'Trần Văn B',
    username: 'tranvanb',
    password: 'password456',
    phone: '0909876543',
    email: 'tranvanb@company.com',
    licenseNumber: 'C-987654321',
    licenseClass: 'C',
    licenseExpiryDate: '2025-06-15',
    status: 'active',
    currentRoute: { id: 'route-002', name: 'Tuyến TP.HCM - Đà Nẵng' },
    currentVehicle: { id: 'vehicle-002', licensePlate: '51B-67890' },
    assignedVehicles: ['vehicle-002'],
    joinDate: '2022-11-15',
    totalTrips: 203,
    rating: 4.6,
    notes: 'Tài xế có kinh nghiệm lái xe tải lớn'
  },
  {
    id: 'driver-003',
    name: 'Lê Văn C',
    username: 'levanc',
    password: 'password789',
    phone: '0912345678',
    email: 'levanc@company.com',
    licenseNumber: 'B2-555666777',
    licenseClass: 'B2',
    licenseExpiryDate: '2027-03-20',
    status: 'active',
    currentRoute: { id: 'route-001', name: 'Tuyến Hà Nội - Hải Phòng' },
    currentVehicle: { id: 'vehicle-003', licensePlate: '30C-11111' },
    assignedVehicles: ['vehicle-003'],
    joinDate: '2023-08-10',
    totalTrips: 89,
    rating: 4.9,
    notes: 'Tài xế trẻ, nhiệt tình'
  },
  {
    id: 'driver-004',
    name: 'Phạm Thị D',
    username: 'phamthid',
    password: 'password000',
    phone: '0923456789',
    email: 'phamthid@company.com',
    licenseNumber: 'B2-888999000',
    licenseClass: 'B2',
    licenseExpiryDate: '2025-11-10',
    status: 'off',
    currentRoute: null,
    currentVehicle: null,
    assignedVehicles: [], // Chưa gán xe nào
    joinDate: '2023-03-15',
    totalTrips: 67,
    rating: 4.7,
    notes: 'Đang nghỉ phép'
  },
  {
    id: 'driver-005',
    name: 'Hoàng Văn E',
    username: 'hoangvane',
    password: 'password111',
    phone: '0934567890',
    email: 'hoangvane@company.com',
    licenseNumber: 'B2-111222333',
    licenseClass: 'B2',
    licenseExpiryDate: '2026-08-15',
    status: 'active',
    currentRoute: null,
    currentVehicle: null,
    assignedVehicles: [], // Tài xế mới, chưa gán xe
    joinDate: '2024-01-20',
    totalTrips: 12,
    rating: 4.5,
    notes: 'Tài xế mới, đang tập sự'
  }
];

// Hàm tính giờ đến dự kiến
const calculateArrivalTime = (departureTime: string, estimatedMinutes: number) => {
  const [hours, minutes] = departureTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + estimatedMinutes;
  const arrivalHours = Math.floor(totalMinutes / 60) % 24;
  const arrivalMinutes = totalMinutes % 60;
  return `${arrivalHours.toString().padStart(2, '0')}:${arrivalMinutes.toString().padStart(2, '0')}`;
};

// Hàm tạo thời tiết ngẫu nhiên
const getRandomWeather = () => {
  const weathers = [
    { condition: 'sunny', icon: '☀️', temp: Math.floor(Math.random() * 10) + 25 },
    { condition: 'cloudy', icon: '☁️', temp: Math.floor(Math.random() * 8) + 22 },
    { condition: 'rainy', icon: '🌧️', temp: Math.floor(Math.random() * 6) + 20 },
    { condition: 'windy', icon: '💨', temp: Math.floor(Math.random() * 8) + 23 }
  ];
  return weathers[Math.floor(Math.random() * weathers.length)];
};

// Mock data cho lịch trình - Cải thiện với dữ liệu phong phú hơn
const generateTimeSlots = (routeId: string) => {
  const slots = [];
  const route = mockRoutes.find(r => r.id === routeId);
  if (!route) return [];

  // Lấy giờ hoạt động từ tuyến đường
  const [startHour, endHour] = route.operatingHours.split(' - ').map(time => parseInt(time.split(':')[0]));
  const frequency = route.roundTripTime; // Tần suất chuyến (phút)
  
  // Tạo các khung giờ dựa trên tần suất
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += frequency) {
      if (hour === endHour && minute > 0) break; // Không vượt quá giờ kết thúc
      
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // Tạo trạng thái thông minh hơn
      let status = 'available';
      let assignedVehicle = null;
      let trip = null;
      let estimatedRevenue = 0;
      let passengerCount = 0;
      
      // Giờ nghỉ trưa (12:00-13:30)
      if (hour >= 12 && hour < 14) {
        status = 'break';
      } 
      // Giờ cao điểm (7-9h sáng, 17-19h chiều) - tỷ lệ gán cao hơn
      else if ((hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 19)) {
        if (Math.random() > 0.2) { // 80% được gán
          status = 'assigned';
          const availableVehicles = mockVehicles.filter(v => v.route?.id === routeId && v.status === 'active');
          if (availableVehicles.length > 0) {
            const randomVehicle = availableVehicles[Math.floor(Math.random() * availableVehicles.length)];
            assignedVehicle = {
              id: randomVehicle.id,
              licensePlate: randomVehicle.licensePlate,
              driver: randomVehicle.driver,
              capacity: randomVehicle.capacity
            };
            trip = {
              id: `trip-${routeId}-${timeString.replace(':', '')}-${new Date().toISOString().slice(8, 10)}`,
              code: `${route.code}-${timeString.replace(':', '')}-${new Date().toISOString().slice(8, 10)}`,
              status: Math.random() > 0.1 ? 'completed' : 'in-progress',
              departureTime: timeString,
              estimatedArrival: calculateArrivalTime(timeString, route.estimatedTime),
              distance: route.distance,
              fuelCost: Math.round(route.distance * 0.8 * 1000), // 800đ/km
              tollCost: Math.round(route.distance * 0.2 * 1000), // 200đ/km
            };
            estimatedRevenue = Math.round(route.distance * 2.5 * 1000); // 2500đ/km
            passengerCount = Math.floor(Math.random() * (randomVehicle.capacity / 100)) + 1; // Giả sử capacity tính bằng kg, chia 100 để ra số người
          }
        }
      }
      // Giờ bình thường - tỷ lệ gán trung bình
      else if (Math.random() > 0.5) { // 50% được gán
        status = 'assigned';
        const availableVehicles = mockVehicles.filter(v => v.route?.id === routeId && v.status === 'active');
        if (availableVehicles.length > 0) {
          const randomVehicle = availableVehicles[Math.floor(Math.random() * availableVehicles.length)];
          assignedVehicle = {
            id: randomVehicle.id,
            licensePlate: randomVehicle.licensePlate,
            driver: randomVehicle.driver,
            capacity: randomVehicle.capacity
          };
          trip = {
            id: `trip-${routeId}-${timeString.replace(':', '')}-${new Date().toISOString().slice(8, 10)}`,
            code: `${route.code}-${timeString.replace(':', '')}-${new Date().toISOString().slice(8, 10)}`,
            status: Math.random() > 0.2 ? 'completed' : 'in-progress',
            departureTime: timeString,
            estimatedArrival: calculateArrivalTime(timeString, route.estimatedTime),
            distance: route.distance,
            fuelCost: Math.round(route.distance * 0.8 * 1000),
            tollCost: Math.round(route.distance * 0.2 * 1000),
          };
          estimatedRevenue = Math.round(route.distance * 2.5 * 1000);
          passengerCount = Math.floor(Math.random() * (randomVehicle.capacity / 100)) + 1;
        }
      }
      
      slots.push({
        timeSlot: timeString,
        status,
        assignedVehicle,
        trip,
        estimatedRevenue,
        passengerCount,
        weather: getRandomWeather(),
        notes: status === 'assigned' ? `Chuyến ${trip?.code}` : ''
      });
    }
  }
  return slots;
};

// Tạo dữ liệu lịch trình cho nhiều tuyến
const generateScheduleData = (routeId: string, date: string) => {
  const route = mockRoutes.find(r => r.id === routeId);
  if (!route) return null;

  const timeSlots = generateTimeSlots(routeId);
  const assignedSlots = timeSlots.filter(slot => slot.status === 'assigned');
  const totalRevenue = assignedSlots.reduce((sum, slot) => sum + slot.estimatedRevenue, 0);
  const totalPassengers = assignedSlots.reduce((sum, slot) => sum + slot.passengerCount, 0);
  const completedTrips = assignedSlots.filter(slot => slot.trip?.status === 'completed').length;
  const inProgressTrips = assignedSlots.filter(slot => slot.trip?.status === 'in-progress').length;

  return {
    routeId: route.id,
    routeName: route.name,
    routeCode: route.code,
    operatingHours: route.operatingHours,
    roundTripTime: route.roundTripTime,
    date: date,
    timeSlots: timeSlots,
    statistics: {
      totalSlots: timeSlots.length,
      assignedSlots: assignedSlots.length,
      availableSlots: timeSlots.filter(slot => slot.status === 'available').length,
      breakSlots: timeSlots.filter(slot => slot.status === 'break').length,
      completedTrips: completedTrips,
      inProgressTrips: inProgressTrips,
      totalRevenue: totalRevenue,
      totalPassengers: totalPassengers,
      averageRevenue: assignedSlots.length > 0 ? Math.round(totalRevenue / assignedSlots.length) : 0,
      utilizationRate: Math.round((assignedSlots.length / (timeSlots.length - timeSlots.filter(slot => slot.status === 'break').length)) * 100)
    }
  };
};

// Khởi tạo dữ liệu lịch trình mặc định
const mockScheduleData = generateScheduleData('route-001', new Date().toISOString().slice(0, 10));

const Routes = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('routes');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'route' | 'vehicle' | 'driver'>('route');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedScheduleRoute, setSelectedScheduleRoute] = useState('route-001');
  
  // Schedule management states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showBreakDialog, setShowBreakDialog] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [selectedVehicleForAssign, setSelectedVehicleForAssign] = useState<string>('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sorting states
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Password visibility states
  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});

  // Detail dialog states
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailType, setDetailType] = useState<'route' | 'vehicle' | 'driver'>('route');

  // Form hooks
  const routeForm = useForm();
  const vehicleForm = useForm();
  const driverForm = useForm();

  // Tạo dữ liệu lịch trình động dựa trên tuyến và ngày được chọn
  const currentScheduleData = generateScheduleData(selectedScheduleRoute, scheduleDate);

  // Helper functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Đã sao chép",
        description: `Đã sao chép "${text}" vào clipboard`,
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast({
        title: "Lỗi",
        description: "Không thể sao chép văn bản",
        variant: "destructive",
      });
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data: any[], field: string, direction: 'asc' | 'desc') => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle nested objects
      if (field.includes('.')) {
        const fields = field.split('.');
        aValue = fields.reduce((obj, key) => obj?.[key], a);
        bValue = fields.reduce((obj, key) => obj?.[key], b);
      }
      
      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const paginateData = (data: any[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalItems: number) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleViewDetail = (type: 'route' | 'vehicle' | 'driver', item: any) => {
    setDetailType(type);
    setSelectedItem(item);
    setShowDetailDialog(true);
  };

  // Filtered data
  const filteredRoutes = mockRoutes.filter(route => 
    route.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVehicles = mockVehicles.filter(vehicle => {
    const matchesSearch = vehicle.licensePlate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRoute = selectedRoute === 'all' || vehicle.route?.id === selectedRoute;
    const matchesStatus = selectedStatus === 'all' || vehicle.status === selectedStatus;
    return matchesSearch && matchesRoute && matchesStatus;
  });

  const filteredDrivers = mockDrivers.filter(driver => {
    const matchesSearch = driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.phone.includes(searchTerm) ||
                         driver.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRoute = selectedRoute === 'all' || driver.currentRoute?.id === selectedRoute;
    const matchesStatus = selectedStatus === 'all' || driver.status === selectedStatus;
    return matchesSearch && matchesRoute && matchesStatus;
  });

  // Handlers
  const handleCreate = (type: 'route' | 'vehicle' | 'driver') => {
    setDialogType(type);
    setSelectedItem(null);
    setShowDialog(true);
  };

  const handleEdit = (type: 'route' | 'vehicle' | 'driver', item: any) => {
    setDialogType(type);
    setSelectedItem(item);
    setShowDialog(true);
  };

  const handleSubmit = (data: any) => {
    console.log(`${dialogType} data:`, data);
    toast({
      title: "Thành công",
      description: `${dialogType === 'route' ? 'Tuyến đường' : dialogType === 'vehicle' ? 'Phương tiện' : 'Tài xế'} đã được ${selectedItem ? 'cập nhật' : 'tạo mới'} thành công.`,
    });
    setShowDialog(false);
  };

  const handleScheduleAssign = (timeSlot: string, vehicleId: string) => {
    // Cập nhật dữ liệu lịch trình (trong thực tế sẽ gọi API)
    const updatedSlots = currentScheduleData.timeSlots.map(slot => {
      if (slot.timeSlot === timeSlot) {
        const vehicle = mockVehicles.find(v => v.id === vehicleId);
        return {
          ...slot,
          status: 'assigned' as const,
          assignedVehicle: vehicle ? {
            id: vehicle.id,
            licensePlate: vehicle.licensePlate,
            driver: vehicle.driver
          } : undefined,
          tripDetails: {
            estimatedArrival: calculateArrivalTime(timeSlot, currentScheduleData.roundTripTime),
            passengers: Math.floor(Math.random() * 30) + 10,
            revenue: Math.floor(Math.random() * 200000) + 50000
          }
        };
      }
      return slot;
    });

    toast({
      title: "Gán xe thành công",
      description: `Đã gán xe vào khung giờ ${timeSlot}`,
    });
    setShowAssignDialog(false);
  };

  const handleScheduleBreak = (timeSlot: string) => {
    // Cập nhật dữ liệu lịch trình (trong thực tế sẽ gọi API)
    const updatedSlots = currentScheduleData.timeSlots.map(slot => {
      if (slot.timeSlot === timeSlot) {
        return {
          ...slot,
          status: 'break' as const,
          assignedVehicle: undefined,
          tripDetails: undefined
        };
      }
      return slot;
    });

    toast({
      title: "Đặt giờ nghỉ thành công",
      description: `Đã đặt khung giờ ${timeSlot} là giờ nghỉ`,
    });
    setShowBreakDialog(false);
  };

  const handleTimeSlotClick = (slot: any) => {
    setSelectedTimeSlot(slot.timeSlot);
    
    if (slot.status === 'available') {
      setShowAssignDialog(true);
    } else if (slot.status === 'assigned') {
      // Hiển thị thông tin chi tiết hoặc cho phép hủy gán
      toast({
        title: "Thông tin chuyến đi",
        description: `Xe ${slot.assignedVehicle?.licensePlate} - Tài xế: ${slot.assignedVehicle?.driver?.name}`,
      });
    }
  };

  const handleSetBreak = (timeSlot: string) => {
    setSelectedTimeSlot(timeSlot);
    setShowBreakDialog(true);
  };

  const handleAssignVehicle = () => {
    if (selectedVehicleForAssign && selectedTimeSlot) {
      handleScheduleAssign(selectedTimeSlot, selectedVehicleForAssign);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Hoạt động', variant: 'default' as const, color: 'bg-green-100 text-green-800' },
      inactive: { label: 'Tạm dừng', variant: 'secondary' as const, color: 'bg-gray-100 text-gray-800' },
      maintenance: { label: 'Bảo trì', variant: 'destructive' as const, color: 'bg-yellow-100 text-yellow-800' },
      off: { label: 'Nghỉ phép', variant: 'outline' as const, color: 'bg-blue-100 text-blue-800' },
      good: { label: 'Tốt', variant: 'default' as const, color: 'bg-green-100 text-green-800' },
      fair: { label: 'Khá', variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' },
      excellent: { label: 'Xuất sắc', variant: 'default' as const, color: 'bg-emerald-100 text-emerald-800' },
      assigned: { label: 'Đã gán', variant: 'default' as const, color: 'bg-blue-100 text-blue-800' },
      available: { label: 'Trống', variant: 'outline' as const, color: 'bg-gray-100 text-gray-600' },
      break: { label: 'Nghỉ', variant: 'secondary' as const, color: 'bg-orange-100 text-orange-800' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getConditionIcon = (condition: string) => {
    switch (condition) {
      case 'excellent': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'good': return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'fair': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default: return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quản lý Tuyến & Xe</h1>
          <p className="text-muted-foreground">
            Quản lý tuyến đường, phương tiện, tài xế và lịch trình hoạt động
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Xuất báo cáo
          </Button>
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Làm mới
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tổng tuyến đường</CardTitle>
            <Route className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockRoutes.length}</div>
            <p className="text-xs text-muted-foreground">
              +2 từ tháng trước
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tổng phương tiện</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockVehicles.length}</div>
            <p className="text-xs text-muted-foreground">
              +1 từ tuần trước
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tổng tài xế</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockDrivers.length}</div>
            <p className="text-xs text-muted-foreground">
              {mockDrivers.filter(d => d.status === 'active').length} đang hoạt động
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Chuyến đi hôm nay</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">47</div>
            <p className="text-xs text-muted-foreground">
              +12% so với hôm qua
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="routes" className="flex items-center space-x-2">
            <Route className="h-4 w-4" />
            <span>Tuyến đường</span>
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="flex items-center space-x-2">
            <Truck className="h-4 w-4" />
            <span>Phương tiện</span>
          </TabsTrigger>
          <TabsTrigger value="drivers" className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>Tài xế</span>
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Lịch trình</span>
          </TabsTrigger>
        </TabsList>

        {/* Routes Tab */}
        <TabsContent value="routes" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tìm kiếm tuyến đường..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-[300px]"
                />
              </div>
            </div>
            <Button onClick={() => handleCreate('route')}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm tuyến đường
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('code')}
                  >
                    <div className="flex items-center">
                      Mã tuyến
                      {sortField === 'code' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Tên tuyến
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Lộ trình</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('distance')}
                  >
                    <div className="flex items-center">
                      Khoảng cách
                      {sortField === 'distance' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Thời gian ước tính</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Trạng thái
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Xe/Tài xế</TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginateData(sortData(filteredRoutes, sortField, sortDirection), currentPage, itemsPerPage).map((route) => (
                  <TableRow key={route.id}>
                    <TableCell 
                      className="font-medium cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(route.code)}
                    >
                      {route.code}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(route.name)}
                    >
                      {route.name}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell 
                      className="max-w-[200px] truncate cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(route.path)}
                    >
                      {route.path}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell>{route.distance} km</TableCell>
                    <TableCell>{Math.floor(route.estimatedTime / 60)}h {route.estimatedTime % 60}m</TableCell>
                    <TableCell>{getStatusBadge(route.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span className="flex items-center">
                          <Truck className="h-3 w-3 mr-1" />
                          {route.vehicleCount}
                        </span>
                        <span className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {route.driverCount}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit('route', route)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Chỉnh sửa
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewDetail('route', route)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Xem chi tiết
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Xóa
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredRoutes.length)} trong tổng số {filteredRoutes.length} tuyến đường
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Trước
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: getTotalPages(filteredRoutes.length, itemsPerPage) }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === getTotalPages(filteredRoutes.length, itemsPerPage) || 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      </div>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(filteredRoutes.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(filteredRoutes.length, itemsPerPage)}
                >
                  Sau
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tìm kiếm phương tiện..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-[300px]"
                />
              </div>
              <Select value={selectedRoute} onValueChange={setSelectedRoute}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Chọn tuyến" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả tuyến</SelectItem>
                  {mockRoutes.map((route) => (
                    <SelectItem key={route.id} value={route.id}>
                      {route.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả</SelectItem>
                  <SelectItem value="active">Hoạt động</SelectItem>
                  <SelectItem value="maintenance">Bảo trì</SelectItem>
                  <SelectItem value="inactive">Tạm dừng</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => handleCreate('vehicle')}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm phương tiện
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('licensePlate')}
                  >
                    <div className="flex items-center">
                      Biển số
                      {sortField === 'licensePlate' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Loại xe</TableHead>
                  <TableHead>Hãng/Model</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('capacity')}
                  >
                    <div className="flex items-center">
                      Tải trọng
                      {sortField === 'capacity' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Tuyến</TableHead>
                  <TableHead>Tài xế</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Trạng thái
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Tình trạng</TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginateData(sortData(filteredVehicles, sortField, sortDirection), currentPage, itemsPerPage).map((vehicle) => (
                  <TableRow key={vehicle.id}>
                    <TableCell 
                      className="font-medium cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(vehicle.licensePlate)}
                    >
                      {vehicle.licensePlate}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell>{vehicle.type.name}</TableCell>
                    <TableCell 
                      className="cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(`${vehicle.brand} ${vehicle.model}`)}
                    >
                      {vehicle.brand} {vehicle.model}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell>{vehicle.capacity} kg</TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {vehicle.route?.name || 'Chưa gán'}
                    </TableCell>
                    <TableCell>
                      {vehicle.driver ? (
                        <div 
                          className="cursor-pointer hover:bg-muted/50 relative group"
                          onClick={() => copyToClipboard(`${vehicle.driver.name} - ${vehicle.driver.phone}`)}
                        >
                          <div className="font-medium">{vehicle.driver.name}</div>
                          <div className="text-sm text-muted-foreground">{vehicle.driver.phone}</div>
                          <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      ) : (
                        'Chưa gán'
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(vehicle.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        {getConditionIcon(vehicle.condition)}
                        {getStatusBadge(vehicle.condition)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit('vehicle', vehicle)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Chỉnh sửa
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewDetail('vehicle', vehicle)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Xem chi tiết
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Settings className="h-4 w-4 mr-2" />
                            Bảo trì
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Xóa
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredVehicles.length)} trong tổng số {filteredVehicles.length} phương tiện
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Trước
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: getTotalPages(filteredVehicles.length, itemsPerPage) }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === getTotalPages(filteredVehicles.length, itemsPerPage) || 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      </div>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(filteredVehicles.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(filteredVehicles.length, itemsPerPage)}
                >
                  Sau
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tìm kiếm tài xế..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-[300px]"
                />
              </div>
              <Select value={selectedRoute} onValueChange={setSelectedRoute}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Chọn tuyến" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả tuyến</SelectItem>
                  {mockRoutes.map((route) => (
                    <SelectItem key={route.id} value={route.id}>
                      {route.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả</SelectItem>
                  <SelectItem value="active">Hoạt động</SelectItem>
                  <SelectItem value="off">Nghỉ phép</SelectItem>
                  <SelectItem value="inactive">Tạm dừng</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => handleCreate('driver')}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm tài xế
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Tên tài xế
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Số điện thoại</TableHead>
                  <TableHead>Bằng lái</TableHead>
                  <TableHead>Tuyến hiện tại</TableHead>
                  <TableHead>Xe hiện tại</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Trạng thái
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('rating')}
                  >
                    <div className="flex items-center">
                      Đánh giá
                      {sortField === 'rating' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('totalTrips')}
                  >
                    <div className="flex items-center">
                      Số chuyến
                      {sortField === 'totalTrips' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginateData(sortData(filteredDrivers, sortField, sortDirection), currentPage, itemsPerPage).map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell>
                      <div 
                        className="cursor-pointer hover:bg-muted/50 relative group"
                        onClick={() => copyToClipboard(`${driver.name} - ${driver.email}`)}
                      >
                        <div className="font-medium">{driver.name}</div>
                        <div className="text-sm text-muted-foreground">{driver.email}</div>
                        <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(driver.phone)}
                    >
                      {driver.phone}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell>
                      <div 
                        className="cursor-pointer hover:bg-muted/50 relative group"
                        onClick={() => copyToClipboard(`${driver.licenseClass} - HSD: ${new Date(driver.licenseExpiryDate).toLocaleDateString('vi-VN')}`)}
                      >
                        <div className="font-medium">{driver.licenseClass}</div>
                        <div className="text-sm text-muted-foreground">
                          HSD: {new Date(driver.licenseExpiryDate).toLocaleDateString('vi-VN')}
                        </div>
                        <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </TableCell>
                    <TableCell 
                      className="max-w-[150px] truncate cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(driver.currentRoute?.name || 'Chưa gán')}
                    >
                      {driver.currentRoute?.name || 'Chưa gán'}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer hover:bg-muted/50 relative group"
                      onClick={() => copyToClipboard(driver.currentVehicle?.licensePlate || 'Chưa gán')}
                    >
                      {driver.currentVehicle?.licensePlate || 'Chưa gán'}
                      <Copy className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </TableCell>
                    <TableCell>{getStatusBadge(driver.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <span className="text-yellow-500">★</span>
                        <span>{driver.rating}</span>
                      </div>
                    </TableCell>
                    <TableCell>{driver.totalTrips}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit('driver', driver)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Chỉnh sửa
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewDetail('driver', driver)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Xem chi tiết
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <User className="h-4 w-4 mr-2" />
                            Gán tuyến/xe
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Xóa
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredDrivers.length)} trong tổng số {filteredDrivers.length} tài xế
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Trước
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: getTotalPages(filteredDrivers.length, itemsPerPage) }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === getTotalPages(filteredDrivers.length, itemsPerPage) || 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      </div>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(filteredDrivers.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(filteredDrivers.length, itemsPerPage)}
                >
                  Sau
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Select value={selectedScheduleRoute} onValueChange={setSelectedScheduleRoute}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Chọn tuyến" />
                </SelectTrigger>
                <SelectContent>
                  {mockRoutes.map((route) => (
                    <SelectItem key={route.id} value={route.id}>
                      {route.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-[150px]"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Xuất lịch trình
              </Button>
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Nhập lịch trình
              </Button>
            </div>
          </div>

          {selectedScheduleRoute && currentScheduleData ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Lịch trình hoạt động - {currentScheduleData.routeName}</span>
                </CardTitle>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <span>Ngày: {new Date(scheduleDate).toLocaleDateString('vi-VN')}</span>
                  <span>Giờ hoạt động: {currentScheduleData.operatingHours}</span>
                  <span>Chu kỳ: {currentScheduleData.roundTripTime} phút</span>
                  <span>Thời tiết: {currentScheduleData.weather}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Time Slots Grid */}
                  <div className="lg:col-span-2">
                    <h3 className="font-semibold mb-3">Bảng thời gian</h3>
                    <div className="grid grid-cols-4 gap-2 max-h-[500px] overflow-y-auto">
                      {currentScheduleData.timeSlots.map((slot) => (
                        <div
                          key={slot.timeSlot}
                          className={`relative p-2 rounded-lg border text-center text-xs cursor-pointer transition-colors group ${
                            slot.status === 'assigned'
                              ? 'bg-blue-100 border-blue-300 text-blue-800'
                              : slot.status === 'break'
                              ? 'bg-orange-100 border-orange-300 text-orange-800'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                          onClick={() => handleTimeSlotClick(slot)}
                        >
                          <div className="font-medium">{slot.timeSlot}</div>
                          {slot.status === 'assigned' && slot.assignedVehicle && (
                            <div className="mt-1">
                              <div className="font-medium">{slot.assignedVehicle.licensePlate}</div>
                              <div className="text-xs">{slot.assignedVehicle.driver?.name}</div>
                              {slot.tripDetails && (
                                <div className="text-xs mt-1 space-y-1">
                                  <div>Đến: {slot.tripDetails.estimatedArrival}</div>
                                  <div>KH: {slot.tripDetails.passengers}</div>
                                  <div className="text-green-600">{slot.tripDetails.revenue.toLocaleString()}đ</div>
                                </div>
                              )}
                            </div>
                          )}
                          {slot.status === 'break' && (
                            <div className="mt-1 text-xs">Nghỉ</div>
                          )}
                          {slot.status === 'available' && (
                            <div className="mt-1 text-xs">Trống</div>
                          )}
                          
                          {/* Context menu cho khung giờ trống */}
                          {slot.status === 'available' && (
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedTimeSlot(slot.timeSlot);
                                    setShowAssignDialog(true);
                                  }}>
                                    <Truck className="h-4 w-4 mr-2" />
                                    Gán xe
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSetBreak(slot.timeSlot)}>
                                    <Clock className="h-4 w-4 mr-2" />
                                    Đặt giờ nghỉ
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                          
                          {/* Context menu cho khung giờ đã gán */}
                          {slot.status === 'assigned' && (
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    // Hủy gán xe
                                    const updatedSlots = currentScheduleData.timeSlots.map(s => 
                                      s.timeSlot === slot.timeSlot 
                                        ? { ...s, status: 'available' as const, assignedVehicle: undefined, tripDetails: undefined }
                                        : s
                                    );
                                    toast({
                                      title: "Hủy gán xe thành công",
                                      description: `Đã hủy gán xe khỏi khung giờ ${slot.timeSlot}`,
                                    });
                                  }}>
                                    <X className="h-4 w-4 mr-2" />
                                    Hủy gán
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSetBreak(slot.timeSlot)}>
                                    <Clock className="h-4 w-4 mr-2" />
                                    Chuyển thành giờ nghỉ
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                          
                          {/* Context menu cho giờ nghỉ */}
                          {slot.status === 'break' && (
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    // Hủy giờ nghỉ
                                    const updatedSlots = currentScheduleData.timeSlots.map(s => 
                                      s.timeSlot === slot.timeSlot 
                                        ? { ...s, status: 'available' as const }
                                        : s
                                    );
                                    toast({
                                      title: "Hủy giờ nghỉ thành công",
                                      description: `Đã hủy giờ nghỉ cho khung giờ ${slot.timeSlot}`,
                                    });
                                  }}>
                                    <X className="h-4 w-4 mr-2" />
                                    Hủy giờ nghỉ
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legend and Controls */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-3">Chú thích</h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                          <span className="text-sm">Đã gán xe</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded"></div>
                          <span className="text-sm">Giờ nghỉ</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded"></div>
                          <span className="text-sm">Khung giờ trống</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Thống kê</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Tổng khung giờ:</span>
                          <span className="font-medium">{currentScheduleData.timeSlots.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Đã gán:</span>
                          <span className="font-medium text-blue-600">
                            {currentScheduleData.timeSlots.filter(s => s.status === 'assigned').length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Giờ nghỉ:</span>
                          <span className="font-medium text-orange-600">
                            {currentScheduleData.timeSlots.filter(s => s.status === 'break').length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Còn trống:</span>
                          <span className="font-medium text-gray-600">
                            {currentScheduleData.timeSlots.filter(s => s.status === 'available').length}
                          </span>
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between">
                            <span>Tổng KH:</span>
                            <span className="font-medium text-green-600">
                              {currentScheduleData.timeSlots
                                .filter(s => s.status === 'assigned' && s.tripDetails)
                                .reduce((sum, s) => sum + (s.tripDetails?.passengers || 0), 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Doanh thu:</span>
                            <span className="font-medium text-green-600">
                              {currentScheduleData.timeSlots
                                .filter(s => s.status === 'assigned' && s.tripDetails)
                                .reduce((sum, s) => sum + (s.tripDetails?.revenue || 0), 0)
                                .toLocaleString()}đ
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Xe khả dụng</h3>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {mockVehicles
                          .filter(v => v.status === 'active' && v.route?.id === selectedScheduleRoute)
                          .map((vehicle) => (
                            <div
                              key={vehicle.id}
                              className="flex items-center justify-between p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                            >
                              <div>
                                <div className="font-medium text-sm">{vehicle.licensePlate}</div>
                                <div className="text-xs text-muted-foreground">
                                  {vehicle.driver?.name}
                                </div>
                                <div className="text-xs text-green-600">
                                  {vehicle.capacity} chỗ
                                </div>
                              </div>
                              <Button size="sm" variant="outline">
                                Gán
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-64">
                <div className="text-center text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Vui lòng chọn tuyến đường để xem lịch trình</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedItem ? 'Chỉnh sửa' : 'Thêm mới'} {
                dialogType === 'route' ? 'tuyến đường' :
                dialogType === 'vehicle' ? 'phương tiện' : 'tài xế'
              }
            </DialogTitle>
          </DialogHeader>

          {dialogType === 'route' && (
            <Form {...routeForm}>
              <form onSubmit={routeForm.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={routeForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mã tuyến</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: HN-HP-01" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tên tuyến</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: Tuyến Hà Nội - Hải Phòng" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={driverForm.control}
                  name="assignedVehicles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gán xe</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn xe để gán (tùy chọn)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Không gán xe</SelectItem>
                          {mockVehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={driverForm.control}
                  name="assignedVehicles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gán xe</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn xe để gán (tùy chọn)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Không gán xe</SelectItem>
                          {mockVehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={routeForm.control}
                  name="path"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lộ trình</FormLabel>
                      <FormControl>
                        <Input placeholder="VD: Hà Nội → Hải Dương → Hải Phòng" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={routeForm.control}
                    name="startPoint"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Điểm đầu</FormLabel>
                        <FormControl>
                          <Input placeholder="Nhập điểm đầu tuyến" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="endPoint"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Điểm cuối</FormLabel>
                        <FormControl>
                          <Input placeholder="Nhập điểm cuối tuyến" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={routeForm.control}
                    name="operatingStartTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Giờ bắt đầu hoạt động</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="operatingEndTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Giờ kết thúc hoạt động</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={routeForm.control}
                    name="breakStartTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Giờ bắt đầu nghỉ</FormLabel>
                        <FormControl>
                          <Input type="time" defaultValue="12:00" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="breakEndTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Giờ kết thúc nghỉ</FormLabel>
                        <FormControl>
                          <Input type="time" defaultValue="13:00" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={routeForm.control}
                    name="distance"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Khoảng cách (km)</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="120.5" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="estimatedTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Thời gian ước tính (phút)</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="150" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={routeForm.control}
                    name="roundTripTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Chu kỳ (phút)</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="30" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={routeForm.control}
                  name="operatingHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Giờ hoạt động</FormLabel>
                      <FormControl>
                        <Input placeholder="VD: 06:00 - 22:00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={routeForm.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ghi chú</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Ghi chú về tuyến đường..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Hủy
                  </Button>
                  <Button type="submit">
                    {selectedItem ? 'Cập nhật' : 'Tạo mới'}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {dialogType === 'vehicle' && (
            <Form {...vehicleForm}>
              <form onSubmit={vehicleForm.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={vehicleForm.control}
                    name="licensePlate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Biển số xe</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: 29A-12345" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={vehicleForm.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Loại xe</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn loại xe" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="type-001">Xe tải nhỏ</SelectItem>
                            <SelectItem value="type-002">Xe tải trung</SelectItem>
                            <SelectItem value="type-003">Xe tải lớn</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={vehicleForm.control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hãng xe</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: Hyundai" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={vehicleForm.control}
                    name="model"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Model</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: Porter H150" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={vehicleForm.control}
                    name="capacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tải trọng (kg)</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="1500" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={vehicleForm.control}
                    name="manufacturingYear"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Năm sản xuất</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="2022" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={vehicleForm.control}
                  name="assignedDriver"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gán tài xế</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn tài xế (tùy chọn)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Không gán tài xế</SelectItem>
                          {mockDrivers.map((driver) => (
                            <SelectItem key={driver.id} value={driver.id}>
                              {driver.name} - {driver.phone}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={vehicleForm.control}
                  name="route"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tuyến hoạt động</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn tuyến" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {mockRoutes.map((route) => (
                            <SelectItem key={route.id} value={route.id}>
                              {route.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={vehicleForm.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ghi chú</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Ghi chú về phương tiện..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Hủy
                  </Button>
                  <Button type="submit">
                    {selectedItem ? 'Cập nhật' : 'Tạo mới'}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {dialogType === 'driver' && (
            <Form {...driverForm}>
              <form onSubmit={driverForm.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={driverForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Họ và tên</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: Nguyễn Văn A" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={driverForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Số điện thoại</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: 0987654321" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={driverForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="VD: driver@company.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={driverForm.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tài khoản</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: driver001" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={driverForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mật khẩu</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              type={showPasswords[field.name] ? "text" : "password"} 
                              placeholder="Nhập mật khẩu" 
                              {...field} 
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={() => togglePasswordVisibility(field.name)}
                            >
                              {showPasswords[field.name] ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={driverForm.control}
                    name="licenseNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Số bằng lái</FormLabel>
                        <FormControl>
                          <Input placeholder="VD: B2-123456789" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={driverForm.control}
                    name="licenseClass"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hạng bằng lái</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn hạng bằng lái" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="B2">B2</SelectItem>
                            <SelectItem value="C">C</SelectItem>
                            <SelectItem value="D">D</SelectItem>
                            <SelectItem value="E">E</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={driverForm.control}
                  name="assignedVehicles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gán xe</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn xe để gán (tùy chọn)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Không gán xe</SelectItem>
                          {mockVehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={driverForm.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ghi chú</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Ghi chú về tài xế..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Hủy
                  </Button>
                  <Button type="submit">
                    {selectedItem ? 'Cập nhật' : 'Tạo mới'}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog gán xe */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Gán xe cho khung giờ</DialogTitle>
            <DialogDescription>
              Chọn xe để gán cho khung giờ {selectedTimeSlot?.time}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Chọn xe:</label>
              <Select onValueChange={setSelectedVehicleForAssign}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Chọn xe khả dụng" />
                </SelectTrigger>
                <SelectContent>
                  {mockVehicles
                    .filter(v => v.status === 'active')
                    .map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.licensePlate} - {vehicle.model} ({vehicle.capacity} chỗ)
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Hủy
              </Button>
              <Button onClick={handleAssignVehicle}>
                Gán xe
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog đặt giờ nghỉ */}
      <Dialog open={showBreakDialog} onOpenChange={setShowBreakDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Đặt giờ nghỉ</DialogTitle>
            <DialogDescription>
              Xác nhận đặt giờ nghỉ cho khung giờ {selectedTimeSlot?.time}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Khung giờ này sẽ được đánh dấu là giờ nghỉ và không thể gán xe.
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowBreakDialog(false)}>
                Hủy
              </Button>
              <Button onClick={handleSetBreak} variant="secondary">
                Đặt giờ nghỉ
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog chi tiết */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailType === 'route' && 'Chi tiết tuyến đường'}
              {detailType === 'vehicle' && 'Chi tiết phương tiện'}
              {detailType === 'driver' && 'Chi tiết tài xế'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && detailType === 'route' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Mã tuyến</label>
                  <p className="text-lg font-semibold">{selectedItem.code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Trạng thái</label>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      selectedItem.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <span className={selectedItem.status === 'active' ? 'text-green-600' : 'text-red-600'}>
                      {selectedItem.status === 'active' ? 'Hoạt động' : 'Ngưng hoạt động'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Tên tuyến</label>
                <p className="text-lg">{selectedItem.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Lộ trình</label>
                <p className="text-base">{selectedItem.route}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Khoảng cách</label>
                  <p className="text-lg">{selectedItem.distance}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Thời gian ước tính</label>
                  <p className="text-lg">{selectedItem.estimatedTime}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Mô tả</label>
                <p className="text-base">{selectedItem.description || 'Không có mô tả'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Ghi chú</label>
                <p className="text-base">{selectedItem.notes || 'Không có ghi chú'}</p>
              </div>
            </div>
          )}

          {selectedItem && detailType === 'vehicle' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Biển số xe</label>
                  <p className="text-lg font-semibold">{selectedItem.licensePlate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Trạng thái</label>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      selectedItem.status === 'active' ? 'bg-green-500' : 
                      selectedItem.status === 'maintenance' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <span className={
                      selectedItem.status === 'active' ? 'text-green-600' : 
                      selectedItem.status === 'maintenance' ? 'text-yellow-600' : 'text-red-600'
                    }>
                      {selectedItem.status === 'active' ? 'Hoạt động' : 
                       selectedItem.status === 'maintenance' ? 'Bảo trì' : 'Ngưng hoạt động'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Loại xe</label>
                  <p className="text-lg">{selectedItem.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Hãng xe</label>
                  <p className="text-lg">{selectedItem.brand}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Model</label>
                  <p className="text-lg">{selectedItem.model}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Năm sản xuất</label>
                  <p className="text-lg">{selectedItem.year}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tải trọng</label>
                  <p className="text-lg">{selectedItem.capacity} chỗ</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Tuyến hoạt động</label>
                  <p className="text-lg">{selectedItem.route || 'Chưa gán tuyến'}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Tài xế được gán</label>
                <p className="text-lg">{selectedItem.assignedDriver || 'Chưa gán tài xế'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Ghi chú</label>
                <p className="text-base">{selectedItem.notes || 'Không có ghi chú'}</p>
              </div>
            </div>
          )}

          {selectedItem && detailType === 'driver' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tên tài xế</label>
                  <p className="text-lg font-semibold">{selectedItem.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Trạng thái</label>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      selectedItem.status === 'active' ? 'bg-green-500' : 
                      selectedItem.status === 'break' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <span className={
                      selectedItem.status === 'active' ? 'text-green-600' : 
                      selectedItem.status === 'break' ? 'text-yellow-600' : 'text-red-600'
                    }>
                      {selectedItem.status === 'active' ? 'Đang làm việc' : 
                       selectedItem.status === 'break' ? 'Nghỉ phép' : 'Không hoạt động'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-lg">{selectedItem.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Số điện thoại</label>
                  <p className="text-lg">{selectedItem.phone}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tài khoản</label>
                  <p className="text-lg">{selectedItem.username}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Số bằng lái</label>
                  <p className="text-lg">{selectedItem.licenseNumber}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Hạng bằng lái</label>
                  <p className="text-lg">{selectedItem.licenseClass}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Hạn sử dụng</label>
                  <p className="text-lg">{selectedItem.licenseExpiry}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tuyến hiện tại</label>
                  <p className="text-lg">{selectedItem.currentRoute || 'Chưa gán tuyến'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Xe hiện tại</label>
                  <p className="text-lg">{selectedItem.currentVehicle || 'Chưa gán xe'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Đánh giá</label>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < selectedItem.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">({selectedItem.rating}/5)</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Số chuyến đã chạy</label>
                  <p className="text-lg">{selectedItem.totalTrips} chuyến</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Xe được gán</label>
                <p className="text-lg">{selectedItem.assignedVehicles || 'Chưa gán xe'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Ghi chú</label>
                <p className="text-base">{selectedItem.notes || 'Không có ghi chú'}</p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Đóng
            </Button>
            <Button onClick={() => {
              setShowDetailDialog(false);
              setDialogType(detailType);
              setShowDialog(true);
            }}>
              Chỉnh sửa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Routes;